import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/UnifiedHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SharedFooter from '../../components/SharedFooter';

const SuccessStoriesProvenTransformations = () => {
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedSize, setSelectedSize] = useState('all');

  // Anonymized case studies as specified
  const caseStudies = [
    {
      id: 1,
      title: 'Large US Bank',
      industry: 'Financial Services',
      vertical: 'Banking',
      teamSize: '500+ employees',
      challenge: 'Needed to establish technology and operations center to reduce costs while improving service quality',
      phases: ['Strategic planning', 'Legal setup', 'Infrastructure deployment', 'Team scaling', 'Operations optimization'],
      timeline: '12 months',
      outcomes: [
        '40% reduction in operational costs',
        '99.9% service level achievement',
        '500+ professionals onboarded',
        'Full regulatory compliance achieved'
      ],
      reference: 'CXO of Financial Services firm',
      icon: 'Building',
      color: 'bg-blue-500'
    },
    {
      id: 2,
      title: 'Global E-commerce Platform',
      industry: 'Technology',
      vertical: 'E-commerce',
      teamSize: '250-500 employees',
      challenge: 'Rapid expansion required scalable customer support and development capabilities in India',
      phases: ['Market analysis', 'Facility setup', 'Talent acquisition', 'Process implementation', 'Performance scaling'],
      timeline: '8 months',
      outcomes: [
        '60% faster time-to-market',
        '24/7 global customer support',
        '300+ technical professionals recruited',
        '35% improvement in customer satisfaction'
      ],
      reference: 'CXO of Technology firm',
      icon: 'ShoppingCart',
      color: 'bg-green-500'
    },
    {
      id: 3,
      title: 'Fortune 500 Manufacturing',
      industry: 'Manufacturing',
      vertical: 'Industrial',
      teamSize: '100-250 employees',
      challenge: 'Digital transformation initiative requiring specialized engineering and analytics capabilities',
      phases: ['Business case development', 'Technology planning', 'Center establishment', 'Team development', 'Digital integration'],
      timeline: '10 months',
      outcomes: [
        '50% improvement in process efficiency',
        'Advanced analytics implementation',
        '200+ specialized engineers hired',
        '35% reduction in total operating costs'
      ],
      reference: 'CXO of Industrial firm',
      icon: 'Factory',
      color: 'bg-purple-500'
    },
    {
      id: 4,
      title: 'Healthcare Technology Provider',
      industry: 'Healthcare',
      vertical: 'HealthTech',
      teamSize: '150-300 employees',
      challenge: 'HIPAA-compliant development center for healthcare applications and data analytics',
      phases: ['Compliance planning', 'Secure infrastructure', 'Specialized hiring', 'Quality systems', 'Regulatory validation'],
      timeline: '14 months',
      outcomes: [
        '100% HIPAA compliance achieved',
        'FDA-validated development processes',
        '180+ healthcare specialists onboarded',
        '45% faster product development cycles'
      ],
      reference: 'CXO of Healthcare firm',
      icon: 'Heart',
      color: 'bg-red-500'
    },
    {
      id: 5,
      title: 'Global Consulting Firm',
      industry: 'Professional Services',
      vertical: 'Consulting',
      teamSize: '50-100 employees',
      challenge: 'Knowledge-intensive delivery center for research, analysis, and client support functions',
      phases: ['Service design', 'Knowledge management setup', 'Expert recruitment', 'Client integration', 'Quality optimization'],
      timeline: '6 months',
      outcomes: [
        '70% increase in research capacity',
        'Multi-timezone client coverage',
        '85+ senior consultants recruited',
        '90% client satisfaction scores'
      ],
      reference: 'CXO of Consulting firm',
      icon: 'Users',
      color: 'bg-yellow-500'
    }
  ];

  const industries = ['all', 'Financial Services', 'Technology', 'Manufacturing', 'Healthcare', 'Professional Services'];
  const teamSizes = ['all', '50-100 employees', '100-250 employees', '150-300 employees', '250-500 employees', '500+ employees'];

  const filteredCaseStudies = caseStudies.filter(study => {
    const industryMatch = selectedIndustry === 'all' || study.industry === selectedIndustry;
    const sizeMatch = selectedSize === 'all' || study.teamSize === selectedSize;
    return industryMatch && sizeMatch;
  });

  return (
    <>
      <Helmet>
        <title>Success Stories - Proven GCC Transformations | Bootminds</title>
        <meta 
          name="description" 
          content="Anonymized case studies of successful Global Capability Center implementations. Real outcomes, proven results, and client transformations." 
        />
        <meta 
          name="keywords" 
          content="GCC success stories, case studies, client transformations, proven results, global capability center outcomes" 
        />
        <link rel="canonical" href="/case-studies" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16 lg:pt-20">
          {/* Hero Section */}
          <section className="py-16 lg:py-20 bg-gradient-to-b from-background to-background-alt content-animate">
            <div className="container-executive">
              <div className="max-w-4xl mx-auto text-center space-y-8">
                <h1 className="font-headline font-headline-bold text-3xl lg:text-5xl text-text-primary hero-title-animate">
                  Success Stories
                </h1>
                <p className="text-lg text-text-secondary leading-relaxed hero-description-animate">
                  Proven transformations and measurable outcomes from our Global Capability Center implementations. 
                  Real clients, real results, complete confidentiality.
                </p>
              </div>
            </div>
          </section>

          {/* Filters */}
          <section className="py-8 bg-card border-b border-border content-section-animate">
            <div className="container-executive">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h2 className="font-body font-body-medium text-text-primary">Filter by:</h2>
                  <select
                    value={selectedIndustry}
                    onChange={(e) => setSelectedIndustry(e.target.value)}
                    className="px-4 py-2 border border-border rounded-lg bg-background text-text-primary hover-lift"
                  >
                    {industries.map(industry => (
                      <option key={industry} value={industry}>
                        {industry === 'all' ? 'All Industries' : industry}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedSize}
                    onChange={(e) => setSelectedSize(e.target.value)}
                    className="px-4 py-2 border border-border rounded-lg bg-background text-text-primary hover-lift"
                  >
                    {teamSizes.map(size => (
                      <option key={size} value={size}>
                        {size === 'all' ? 'All Team Sizes' : size}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-sm text-text-secondary">
                  {filteredCaseStudies.length} case studies found
                </div>
              </div>
            </div>
          </section>

          {/* Case Studies Cards */}
          <section className="py-16 content-section-animate">
            <div className="container-executive">
              <div className="grid grid-cols-1 gap-8">
                {filteredCaseStudies.map((study, index) => (
                  <div 
                    key={study.id} 
                    className={`bg-card border border-border rounded-2xl p-8 card-animate hover-lift`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="grid lg:grid-cols-3 gap-8">
                      {/* Study Overview */}
                      <div className="lg:col-span-1 space-y-4">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 ${study.color} rounded-xl flex items-center justify-center`}>
                            <Icon name={study.icon} size={24} color="white" />
                          </div>
                          <div>
                            <h3 className="font-headline font-headline-bold text-xl text-text-primary">
                              {study.title}
                            </h3>
                            <p className="text-sm text-text-secondary">{study.vertical}</p>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <Icon name="Building" size={16} color="var(--color-accent)" />
                            <span className="text-sm text-text-secondary">Industry: {study.industry}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Icon name="Users" size={16} color="var(--color-accent)" />
                            <span className="text-sm text-text-secondary">Team: {study.teamSize}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Icon name="Clock" size={16} color="var(--color-accent)" />
                            <span className="text-sm text-text-secondary">Timeline: {study.timeline}</span>
                          </div>
                        </div>
                      </div>

                      {/* Challenge & Implementation */}
                      <div className="lg:col-span-1 space-y-6">
                        <div>
                          <h4 className="font-body font-body-medium text-text-primary mb-3">Main Challenge</h4>
                          <p className="text-sm text-text-secondary leading-relaxed">{study.challenge}</p>
                        </div>
                        
                        <div>
                          <h4 className="font-body font-body-medium text-text-primary mb-3">Implementation Phases</h4>
                          <ul className="space-y-2">
                            {study.phases.map((phase, index) => (
                              <li key={index} className="flex items-start space-x-2">
                                <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center mt-0.5">
                                  <span className="text-xs font-medium text-text-primary">{index + 1}</span>
                                </div>
                                <span className="text-sm text-text-secondary">{phase}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Outcomes & Reference */}
                      <div className="lg:col-span-1 space-y-6">
                        <div>
                          <h4 className="font-body font-body-medium text-text-primary mb-3">Key Outcomes</h4>
                          <ul className="space-y-2">
                            {study.outcomes.map((outcome, index) => (
                              <li key={index} className="flex items-start space-x-2">
                                <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5" />
                                <span className="text-sm text-text-secondary">{outcome}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="bg-background-alt rounded-xl p-4">
                          <div className="flex items-center space-x-2">
                            <Icon name="User" size={16} color="var(--color-accent)" />
                            <span className="text-sm font-body-medium text-text-primary">Client Reference</span>
                          </div>
                          <p className="text-sm text-text-secondary mt-2">{study.reference}</p>
                          <p className="text-xs text-text-secondary mt-1 italic">
                            *Contact details confidential as per agreement
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredCaseStudies.length === 0 && (
                <div className="text-center py-12">
                  <Icon name="Search" size={48} color="var(--color-text-secondary)" className="mx-auto mb-4" />
                  <h3 className="font-headline font-headline-bold text-xl text-text-primary mb-2">
                    No Results Found
                  </h3>
                  <p className="text-text-secondary">
                    Try adjusting your filters to see more case studies.
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Overall Results Summary */}
          <section className="py-16 bg-background-alt">
            <div className="container-executive">
              <div className="max-w-4xl mx-auto text-center space-y-8">
                <h2 className="font-headline font-headline-bold text-2xl lg:text-3xl text-text-primary">
                  Aggregate Results Across All Implementations
                </h2>
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="bg-card border border-border rounded-xl p-6 text-center">
                    <div className="text-3xl font-headline font-headline-bold text-text-primary mb-2">5</div>
                    <div className="text-sm text-text-secondary">Successful GCCs</div>
                  </div>
                  <div className="bg-card border border-border rounded-xl p-6 text-center">
                    <div className="text-3xl font-headline font-headline-bold text-text-primary mb-2">1,000+</div>
                    <div className="text-sm text-text-secondary">Professionals Placed</div>
                  </div>
                  <div className="bg-card border border-border rounded-xl p-6 text-center">
                    <div className="text-3xl font-headline font-headline-bold text-text-primary mb-2">$20M+</div>
                    <div className="text-sm text-text-secondary">Client Savings Delivered</div>
                  </div>
                  <div className="bg-card border border-border rounded-xl p-6 text-center">
                    <div className="text-3xl font-headline font-headline-bold text-text-primary mb-2">10</div>
                    <div className="text-sm text-text-secondary">Months to Full Scale</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 lg:py-20">
            <div className="container-executive">
              <div className="max-w-3xl mx-auto text-center space-y-8 bg-card border border-border rounded-2xl p-8">
                <h2 className="font-headline font-headline-bold text-2xl lg:text-3xl text-text-primary">
                  Ready to Create Your Success Story?
                </h2>
                <p className="text-text-secondary">
                  Join our portfolio of successful GCC implementations. Let's discuss how we can help you achieve similar results.
                </p>
                <Button
                  variant="default"
                  size="lg"
                  className="bg-cta text-cta-foreground"
                  iconName="Calendar"
                  iconPosition="left"
                  asChild
                >
                  <a href="/contact">
                    Start Your Success Story
                  </a>
                </Button>
              </div>
            </div>
          </section>
        </main>

        <SharedFooter variant="default" />
      </div>
    </>
  );
};

export default SuccessStoriesProvenTransformations;